import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    ScopedAuditTable,
    type AuditActorOption,
    type AuditRow,
} from '../../components/scoped/ScopedAuditTable';
import { TENANT_DESCRIPTOR } from '../../components/scoped/scopeDescriptors';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { shortId } from '../../lib/shortId';
import { auditTargetLabel } from '../../lib/auditTargetLabels';

function TenantAuditPage() {
    const { user } = useAuth();
    const tid = user?.tenantId ?? '';

    // El audit log NO guarda el nombre del actor (solo actorCognitoSub). Resolvemos el nombre con la
    // lista de usuarios del tenant (best-effort): si el actor es staff de plataforma o no está en la
    // lista, cae al cognitoSub. pageSize alto para cubrir el padrón del tenant en una página.
    const { data: usersData } = useQuery({
        queryKey: ['tenant', tid, 'users', 'audit-actor-map'],
        queryFn: () => tenantApi.listUsers(tid, { pageSize: 200 }),
        enabled: !!tid,
    });
    const actorNameBySub = useMemo(() => {
        const map = new Map<string, string>();
        for (const u of usersData?.items ?? []) map.set(u.cognitoSub, u.displayName);
        return map;
    }, [usersData]);
    const actorOptions = useMemo<AuditActorOption[]>(
        () =>
            [...actorNameBySub]
                .filter(([, name]) => name.trim())
                .map(([value, label]) => ({ value, label })),
        [actorNameBySub],
    );

    // Mapa para resolver el Target a nombres legibles (en vez del UUID): roles del tenant.
    const { data: rolesData } = useQuery({
        queryKey: ['tenant', tid, 'roles', 'audit-target-map'],
        queryFn: () => tenantApi.listRoles(tid),
        enabled: !!tid,
    });
    const roleNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of rolesData?.items ?? []) map.set(r.roleId, r.roleName);
        return map;
    }, [rolesData]);

    const resolveActor = useCallback(
        (row: AuditRow): string => {
            const sub = row.actor?.cognitoSub;
            return (
                (sub && actorNameBySub.get(sub)) || row.actor?.email || (sub ? shortId(sub) : '—')
            );
        },
        [actorNameBySub],
    );

    // Recurso legible: tipo en español y el nombre de la entidad si hay listado; si no, el id corto.
    const resolveTarget = useCallback(
        (row: AuditRow): string => {
            const type = row.target?.type ?? '';
            const id = row.target?.id ?? '';
            const namesByType: Partial<Record<string, Map<string, string>>> = {
                TenantUser: actorNameBySub,
                Role: roleNameById,
            };
            return auditTargetLabel(type, id, namesByType[type]?.get(id));
        },
        [actorNameBySub, roleNameById],
    );

    return (
        <ScopedAuditTable
            descriptor={TENANT_DESCRIPTOR}
            resolveActor={resolveActor}
            resolveTarget={resolveTarget}
            actorOptions={actorOptions}
        />
    );
}

export const Route = createFileRoute('/_authenticated/_layout/tenant/audit')({
    component: TenantAuditPage,
});
